/**
 * Estimate "Next Steps" action-button bar — shared config types + defaults.
 *
 * Mirrors the server contract in public/api/action-buttons-helper.php. The bar
 * is admin-configurable (a universal default set + optional per-retailer
 * overrides) and delivered in the products.php?pricing=1 payload, but this file
 * also holds the built-in fallback used when no config is available and the
 * registry that drives the admin action-type picker.
 */

export type ActionButtonVariant = 'default' | 'primary' | 'secondary';

export type ActionButtonIcon =
  | { type: 'lucide'; name: string }
  | { type: 'upload'; url: string };

/** Discriminated union of what a button does. New types = a new case here + in the dispatcher. */
export type ActionSpec =
  | { type: 'pdf' }
  | { type: 'print' }
  | { type: 'comments' }
  /** Open the share picker (send the estimate to retailer / installer locations). */
  | { type: 'share' }
  | { type: 'link'; url: string; target: '_self' | '_blank' }
  | {
      type: 'modal';
      title?: string;
      contentHtml: string;
      /** Bootstrap modal width. Omitted / 'md' = default. */
      size?: 'sm' | 'md' | 'lg' | 'xl';
      /** Title-bar colours (hex). Omitted = brand default. */
      headerBg?: string;
      headerFg?: string;
    }
  | { type: 'ajax'; endpointId: string; confirm?: string; successMessage?: string }
  | {
      /** Open a location map (retailers / installers) configured under /admin/maps. */
      type: 'map';
      mapId: number;
      title?: string;
      size?: 'lg' | 'xl';
      headerBg?: string;
      headerFg?: string;
    };

export type ActionType = ActionSpec['type'];

export interface ActionButtonConfig {
  id: string;
  label: string;
  icon: ActionButtonIcon;
  variant: ActionButtonVariant;
  order: number;
  enabled: boolean;
  /** Hide the button until the estimate has been saved (has a guid). */
  requiresSavedEstimate?: boolean;
  /**
   * i18n key for `label`, present only on the built-in defaults below. Buttons
   * loaded from estimate_action_bars carry admin-authored text and no key, so
   * they render `label` verbatim until the Phase 3 content overlay covers them.
   */
  labelKey?: string;
  action: ActionSpec;
}

/**
 * Runtime fallback — mirrors abDefaultButtons() in PHP. Rendered when the
 * payload carries no bar (pre-migration / offline) so the "Next Steps" card is
 * never empty. Icons are lucide names rendered dynamically.
 */
export const DEFAULT_ACTION_BUTTONS: ActionButtonConfig[] = [
  { id: 'save-pdf', label: 'Save as PDF', labelKey: 'results:actions.savePdf', icon: { type: 'lucide', name: 'download' }, variant: 'default', order: 0, enabled: true, requiresSavedEstimate: false, action: { type: 'pdf' } },
  { id: 'print', label: 'Print Estimate', labelKey: 'results:actions.print', icon: { type: 'lucide', name: 'printer' }, variant: 'default', order: 1, enabled: true, requiresSavedEstimate: false, action: { type: 'print' } },
  { id: 'find-retailer', label: 'Find a Retailer', labelKey: 'results:actions.findRetailer', icon: { type: 'lucide', name: 'map-pin' }, variant: 'primary', order: 2, enabled: true, requiresSavedEstimate: false, action: { type: 'link', url: 'https://trusscore.com/where-to-buy', target: '_blank' } },
  { id: 'share-estimate', label: 'Send to a Retailer', labelKey: 'results:share.action', icon: { type: 'lucide', name: 'send' }, variant: 'default', order: 3, enabled: true, requiresSavedEstimate: true, action: { type: 'share' } },
  { id: 'comments', label: 'Comments', labelKey: 'results:comments.title', icon: { type: 'lucide', name: 'message-square' }, variant: 'default', order: 4, enabled: true, requiresSavedEstimate: false, action: { type: 'comments' } },
];

/** Metadata for the admin action-type dropdown. `phase2` types are stored but not yet wired at runtime. */
export interface ActionTypeMeta {
  type: ActionType;
  label: string;
  description: string;
  /** lucide icon name shown next to the option. */
  icon: string;
  phase2?: boolean;
}

export const ACTION_TYPE_REGISTRY: ActionTypeMeta[] = [
  { type: 'pdf', label: 'Download PDF', description: 'Generate and download the estimate PDF.', icon: 'download' },
  { type: 'print', label: 'Print', description: 'Open the browser print dialog.', icon: 'printer' },
  { type: 'comments', label: 'Scroll to Comments', description: 'Jump to the comments section on the page.', icon: 'message-square' },
  { type: 'share', label: 'Share with a Retailer / Installer', description: 'Open the share picker (map) so the customer can send the saved estimate to retailer or installer locations. Hidden until the estimate is saved and when sharing is off.', icon: 'send' },
  { type: 'link', label: 'Open a Link', description: 'Open an internal or external URL.', icon: 'external-link' },
  { type: 'modal', label: 'Open a Modal', description: 'Show a popup with custom rich-text content.', icon: 'square-mouse-pointer' },
  { type: 'map', label: 'Open a Location Map', description: 'Show retailer or installer locations on a map in a popup.', icon: 'map' },
  { type: 'ajax', label: 'Webhook / AJAX', description: 'Call a pre-approved server endpoint (coming soon).', icon: 'webhook', phase2: true },
];

/** Default action spec when the admin switches a button to a given type. */
export function defaultActionForType(type: ActionType): ActionSpec {
  switch (type) {
    case 'link': return { type: 'link', url: '', target: '_blank' };
    case 'modal': return { type: 'modal', title: '', contentHtml: '', size: 'md' };
    case 'map': return { type: 'map', mapId: 0, size: 'xl' };
    case 'ajax': return { type: 'ajax', endpointId: '' };
    default: return { type };
  }
}
